import { lemmaCandidates } from './lemma';

/**
 * 复习卡片的文本处理（纯函数）。
 *
 * 复习时最有价值的信息不是"这个词的中文意思"，而是**你第一次遇见它的那句话** ——
 * 语境记忆比词表记忆牢固得多。因此复习卡需要三样文本：
 * 1. `context`：原句（可能被截断到合理长度）；
 * 2. `cloze`：把目标词挖掉的同一句话，用于"填空/回译"式主动回忆；
 * 3. 高亮：卡片上把目标词标出来（正面不标，翻面才标）。
 */

/**
 * 生成挖空句：把句中的目标词换成等长的下划线。
 *
 * 关键点是**必须按词形匹配而不是按字符串匹配**：
 * 用户在文中点的是 `studies`，而生词本里存的是 `study`，
 * 直接 `replace('study', '____')` 在这句话里根本替换不到任何东西。
 * 所以这里用 `lemmaCandidates` 把两边都归并到候选集合再比对。
 *
 * @param sentence 原句
 * @param surface  目标词（生词本里的拼写形式）
 * @returns 挖空后的句子；句中找不到目标词时返回 `null`（宁可不显示，也不显示一句没挖空的"假填空"）
 */
export function buildCloze(sentence: string, surface: string): string | null {
  const target = new Set(lemmaCandidates(surface));
  if (target.size === 0) return null;

  let replaced = 0;
  // 用与 tokenize 相同的词边界规则，保证"挖掉的是词，不是词里的字母"
  const result = sentence.replace(/[A-Za-z]+(?:['’-][A-Za-z]+)*/g, (match) => {
    if (replaced > 0) return match; // 只挖第一个命中，避免一句话里出现三个空格
    const candidates = lemmaCandidates(match);
    if (!candidates.some((candidate) => target.has(candidate))) return match;
    replaced += 1;
    return '_'.repeat(Math.max(4, Math.min(12, match.length)));
  });

  return replaced === 0 ? null : result;
}

/** 遮住目标词（卡片正面用）：与挖空不同，它保留首字母给一点提示。 */
export function maskWord(surface: string): string {
  if (surface.length <= 2) return `${surface[0] ?? ''}_`;
  return `${surface[0]}${'_'.repeat(surface.length - 1)}`;
}

/**
 * 截取上下文片段。
 *
 * @param text      完整句子或段落
 * @param charStart 目标词起点（相对 text）
 * @param charEnd   目标词终点
 * @param radius    目标词左右各保留多少字符
 */
export function contextSnippet(text: string, charStart: number, charEnd: number, radius = 80): string {
  if (!Number.isFinite(charStart) || !Number.isFinite(charEnd) || charEnd <= charStart) {
    return text.length > radius * 2 ? `${text.slice(0, radius * 2)}…` : text;
  }
  const start = Math.max(0, charStart - radius);
  const end = Math.min(text.length, charEnd + radius);
  const head = text.slice(start, charStart);
  const word = text.slice(charStart, charEnd);
  const tail = text.slice(charEnd, end);
  return `${start > 0 ? '…' : ''}${head}${word}${tail}${end < text.length ? '…' : ''}`;
}

/** 在一段文本里把目标词标出来（返回三段，交给 React 渲染，避免 dangerouslySetInnerHTML）。 */
export interface HighlightParts {
  before: string;
  match: string;
  after: string;
}

export function highlightWord(text: string, surface: string): HighlightParts | null {
  const target = new Set(lemmaCandidates(surface));
  if (target.size === 0) return null;

  const re = /[A-Za-z]+(?:['’-][A-Za-z]+)*/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (lemmaCandidates(match[0]).some((candidate) => target.has(candidate))) {
      return {
        before: text.slice(0, match.index),
        match: match[0],
        after: text.slice(match.index + match[0].length),
      };
    }
  }
  return null;
}

/**
 * 复习卡片的"提示等级"。
 *
 * 产品上有个真实的取舍：卡片正面给多少提示？
 * - 给多了（首字母 + 例句 + 音标）→ 用户只是"认出来"，不是"想起来"；
 * - 给少了 → 完全想不起来，挫败感强，直接点"忘记"。
 *
 * 因此按熟悉度分档：不熟的卡给首字母，半熟的只给词性，熟悉的什么都不给。
 */
export function hintLevelFor(familiarity: number): 'initial' | 'pos' | 'none' {
  if (familiarity < 30) return 'initial';
  if (familiarity < 70) return 'pos';
  return 'none';
}
